import { useState, useEffect, useCallback, useRef } from "react";
import i18n from "i18next";
import {
  fetchBulkAnimateActive,
  startBulkAnimate,
  cancelBulkAnimate,
  ackBulkAnimateApplied,
} from "../../common/server_utils";
import { applyBulkAnimationResults } from "../utils/applyBulkAnimationResults.es6";
import { deriveCardLiveState } from "../utils/animatorCardStatus.es6";
import { onJobsSettled, getSettledOutcome } from "../utils/jobSettled";

// Bir batch'in TÜM alt işleri sonuçlandı (done/failed) ve sonucu olan her alt
// iş başka yerden kapandı mı (tepsiden ya da kartın kendi Approve'uyla
// onaylandı / reddedildi)? O zaman uygulanacak bir şey kalmamıştır: "Bu sahneye
// uygula" yeniden çıkmasın diye batch ack'lenir. Sonucu olan en az bir iş şart
// (tamamen başarısız batch'i kullanıcı kendisi kapatır).
export function fullySettledBatchIds(items, outcomeOf = getSettledOutcome) {
  const byBatch = new Map();
  (items || []).forEach((it) => {
    if (!it?.batchId) return;
    if (!byBatch.has(it.batchId)) byBatch.set(it.batchId, []);
    byBatch.get(it.batchId).push(it);
  });
  const out = [];
  byBatch.forEach((list, batchId) => {
    const states = list.map((it) => deriveCardLiveState(it));
    if (!states.every((st) => st.isDone || st.isFailed)) return;
    const done = list.filter((_it, i) => states[i].isDone);
    if (!done.length) return;
    if (done.every((it) => it.jobId && outcomeOf(it.jobId))) out.push(batchId);
  });
  return out;
}

// useBulkAnimateBatch — polling / start / cancel / apply for AnimatorPanel's
// per-scene bulk-animate flow. The training can now run SEVERAL bulk batches
// at once (one per scene); /active aggregates their children into a single
// `items` list, each tagged with its `batchId`. This hook keeps that flat
// items list and exposes scene-scoped cancel/apply — the panel derives each
// scene's phase (idle/running/terminal) from the items whose navId matches.
// One 4s poll loop while anything is pending.

const getTrainingId = () =>
  window.idealStudioTrainingId || new URLSearchParams(window.location.search).get("id");

export function useBulkAnimateBatch({ dispatch, onApplied } = {}) {
  // Flat list of live items across ALL non-approved batches. Each item carries
  // navId + batchId, so the panel groups by scene for progress and this hook
  // groups by scene → batchId for cancel/apply.
  const [items, setItems] = useState([]);
  const [anyPending, setAnyPending] = useState(false);
  const [starting, setStarting] = useState(false);
  // navId currently being applied (null when none) — lets the panel disable
  // just that scene's Apply button without a global lock.
  const [applyingNavId, setApplyingNavId] = useState(null);
  const [error, setError] = useState(null);

  // Re-syncs from the server. Adopts every non-approved batch's items,
  // including terminal-but-unapplied ones (so reopening the panel still shows
  // an apply-pending scene) — per-scene phase makes a terminal scene safe to
  // show without locking the others.
  const checkActive = useCallback(() => {
    const trainingId = getTrainingId();
    if (!trainingId) return Promise.resolve(null);
    return fetchBulkAnimateActive(trainingId).then((data) => {
      if (!data) return null;
      setItems(data.items || []);
      setAnyPending(!!data.active);
      return data;
    });
  }, []);

  // Başka yerden tamamen kapanmış batch'leri ack'le (bir kez), sonra resync.
  const ackedRef = useRef(new Set());
  const ackSettledBatches = useCallback(
    (list) => {
      const ids = fullySettledBatchIds(list).filter((id) => !ackedRef.current.has(id));
      if (!ids.length) return;
      ids.forEach((id) => ackedRef.current.add(id));
      Promise.all(ids.map((id) => ackBulkAnimateApplied(id)))
        .then(() => checkActive())
        .catch(() => {});
    },
    [checkActive]
  );
  useEffect(() => {
    ackSettledBatches(items);
  }, [items, ackSettledBatches]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => onJobsSettled(() => ackSettledBatches(itemsRef.current)), [ackSettledBatches]);

  // Poll while any batch is running — no socket channel for this feature yet.
  useEffect(() => {
    if (!anyPending) return undefined;
    let cancelled = false;
    const id = setInterval(() => {
      if (!cancelled) checkActive();
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [anyPending, checkActive]);

  const start = useCallback(
    async (newItems, opts = {}) => {
      const trainingId = getTrainingId();
      if (!trainingId || !newItems?.length) return null;
      setStarting(true);
      setError(null);
      const result = await startBulkAnimate(trainingId, { items: newItems, ...opts });
      setStarting(false);
      if (!result?.batchId) {
        // 409 (this scene already has a running batch, started elsewhere) or
        // a real error — resync to whatever is actually live server-side.
        const data = await checkActive();
        if (!data) setError(i18n.t("Error"));
        return null;
      }
      await checkActive();
      return result;
    },
    [checkActive]
  );

  // Cancel every batch that has a live item in this scene (normally one).
  const cancelScene = useCallback(
    async (navId) => {
      const batchIds = [
        ...new Set(
          items
            .filter((it) => it.navId === navId)
            .map((it) => it.batchId)
            .filter(Boolean)
        ),
      ];
      if (!batchIds.length) return;
      if (!window.confirm(i18n.t("AnimateScope.CancelConfirm"))) return;
      await Promise.all(batchIds.map((id) => cancelBulkAnimate(id)));
      await checkActive();
    },
    [items, checkActive]
  );

  // Apply just this scene's completed results, ack its batch(es), then resync.
  // Each batch is single-scene (the panel starts one batch per scene), so
  // acking a scene's batchId never marks another scene's work approved.
  const applyScene = useCallback(
    async (navId) => {
      const sceneItems = items.filter((it) => it.navId === navId);
      if (!sceneItems.length) return;
      setApplyingNavId(navId);
      const completed = sceneItems
        .filter((it) => it.resultUrl)
        .map((it) => ({ boxId: it.boxId, navId: it.navId, resultUrl: it.resultUrl }));
      if (completed.length && dispatch) {
        dispatch(applyBulkAnimationResults(completed));
      }
      const batchIds = [...new Set(sceneItems.map((it) => it.batchId).filter(Boolean))];
      await Promise.all(batchIds.map((id) => ackBulkAnimateApplied(id)));
      // ImageAnimatorModal may not be mounted — signal via window event so its
      // version-history cache refetches next time it opens.
      window.dispatchEvent(new CustomEvent("bulkAnimateApplied"));
      setApplyingNavId(null);
      await checkActive();
      if (onApplied) onApplied();
    },
    [items, dispatch, onApplied, checkActive]
  );

  return {
    items,
    anyPending,
    starting,
    applyingNavId,
    error,
    setError,
    checkActive,
    start,
    cancelScene,
    applyScene,
  };
}
